import React, { useRef, useState } from 'react';

import cn from 'classnames';

import cls from './FileDropZone.module.css';

export interface FileDropZoneProps {
    children:
        | React.ReactNode
        | ((helpers: { openFileDialog: () => void; isDragActive: boolean }) => React.ReactNode);

    onFilesDrop: (files: File[]) => void;

    className?: string;
    dragActiveClassName?: string;

    overlayText?: string;
    overlayHintClassName?: string;

    multiple?: boolean;
    accept?: string;

    disabled?: boolean;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
    children,
    onFilesDrop,
    className,
    dragActiveClassName,
    overlayText = 'Отпустите для загрузки',
    overlayHintClassName,
    multiple = true,
    accept,
    disabled = false,
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const dragCounterRef = useRef(0);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const openFileDialog = () => {
        if (disabled) return;
        fileInputRef.current?.click();
    };

    const safeNormalizeFiles = (filesLike: FileList | File[]) => {
        const raw = Array.from(filesLike || []);
        if (!raw.length) return [];
        return raw;
    };

    const handleFiles = (filesLike: FileList | File[]) => {
        const files = safeNormalizeFiles(filesLike);
        if (!files.length) return;
        onFilesDrop(files);
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();

        dragCounterRef.current += 1;

        if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
            setIsDragActive(true);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();

        event.dataTransfer.dropEffect = 'copy';
        if (!isDragActive) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();

        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            setIsDragActive(false);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();

        dragCounterRef.current = 0;
        setIsDragActive(false);
        handleFiles(event.dataTransfer.files);
    };

    const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();

        if (!event.target.files) return;
        handleFiles(event.target.files);
        event.target.value = '';
    };

    const content =
        typeof children === 'function' ? children({ openFileDialog, isDragActive }) : children;

    return (
        <div
            className={cn(cls.FileDropZone, className, disabled && cls.disabled)}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {content}

            <span
                className={cn(
                    cls.overlayHint,
                    overlayHintClassName,
                    isDragActive && cls.overlayVisible
                )}
            >
                {overlayText}
            </span>
            <div
                className={cn(
                    isDragActive && cls.isDragActive,
                    isDragActive && dragActiveClassName
                )}
            />

            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                multiple={multiple}
                accept={accept}
                onChange={handleInputChange}
            />
        </div>
    );
};
